import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import React, { useRef, useState, useEffect } from "react";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { ResourceType } from "shared/types/resource";
import { FileText, CaretDown, CaretUp, Folder, LinkSimple, User } from "@phosphor-icons/react";
import { UniversalPickerModal } from "shared/components/UniversalPickerModal";
import { correctImageOrientation } from "shared/utils/imageOrientation";
import { ResourceChip, ResourceData } from "shared/components/UniversalPickerModal/components/items/ResourceItem";
import * as Sentry from "@sentry/react";

// Quick solution: Define rental-related labels that we know need contract linking
// TODO: Future-proof this by reading from database file labels and their asset linking configuration
const RENTAL_LABELS = [
  "Huurcontract",
  "Bijlage aan huurcontract",
  "Addendum aan huurcontract",
  "Huurhernieuwingsaanvraag",
  "Huurwaarborg",
  "Ingaande plaatsbeschrijving",
  "Uitgaande plaatsbeschrijving",
  "Identiteitskaart huurder",
  "Betalingsbewijs huur",
  "Indexatiebrief",
  "Brandverzekering huurder",
  "Communicatie huurder",
  "Registratiebewijs huurcontract",
  "Opzegbrief",
  "Registratiebewijs plaatsbeschrijving",
  "Reglement inwendige orde",
  "Communicatie - Vredegerecht",
  "Vonnis - Vredegerecht",
  "Verzoeksschrift - Vredegerecht",
  "Dagvaarding - Vredegerecht",
  "Huurgeschil - stukkenbundel intern",
  "Huurgeschil - stukkenbundel huurder",
  "Communicatie huurgeschil - Eigen advocaat",
  "Communicatie huurgeschil - Advocaat huurder"
];

interface FileUploadProps {
  resourceType: ResourceType;
  resource: any;
  onUploadSuccess: () => void;
  initialFiles?: File[];
  fileLabel?: string;
  unitId?: string;
  buildingId?: string;
  mode?: 'document' | 'picture'; // Add mode prop to control UI type
  onClose?: () => void; // Callback for back/close button in mobile sticky header
  customHeaderTitle?: string; // Custom title for mobile sticky header
  assetId?: string; // Asset ID to connect uploaded files to asset
  customData?: any; // Custom data to include in file upload
}

// Simple utility function to check if a label requires rental contract linking
const isRentalRelatedDocument = (label: string): boolean => {
  return RENTAL_LABELS.includes(label);
};

const FileUploadComponent: React.FC<FileUploadProps> = ({
  onUploadSuccess,
  initialFiles = [],
  fileLabel,
  unitId,
  buildingId,
  mode = 'document', // Default to document mode
  onClose,
  customHeaderTitle = "Upload foto's",
  assetId,
  customData: propsCustomData,
}) => {
  const { session } = useSession();
  const dropZoneRef = useRef(null);
  const fileIdCounter = useRef(0); // Counter to ensure unique IDs

  // State for file management and rental contract linking
  const [selectedFiles, setSelectedFiles] = useState<Array<{
    file: File;
    id: string;
    rentalContract?: any;
    status: 'pending' | 'uploading' | 'success' | 'error';
    isSelected?: boolean; // For picture mode selection
  }>>([]);
  const [selectedIndexes, setSelectedIndexes] = useState<Set<number>>(new Set());
  const [isContractPickerOpen, setIsContractPickerOpen] = useState(false);
  const [editingFileId, setEditingFileId] = useState<string | null>(null);
  const customData = propsCustomData || {};
  const [visibleImageIndex, setVisibleImageIndex] = useState<number>(0);
  const imageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [isProcessingImages, setIsProcessingImages] = useState(false);
  const [isLinkedDossiersExpanded, setIsLinkedDossiersExpanded] = useState(true);
  const [isAcquisitionPickerOpen, setIsAcquisitionPickerOpen] = useState(false);
  const [selectedAcquisitionFile, setSelectedAcquisitionFile] = useState<any>(
    customData?.acquisitionId ? { id: customData.acquisitionId, name: `ACQ-${customData.acquisitionId}` } : null
  );

  // State for relations (linked contacts/companies)
  const [isRelationsPickerOpen, setIsRelationsPickerOpen] = useState(false);
  const [selectedRelations, setSelectedRelations] = useState<ResourceData[]>([]);

  // Determine if current file label requires rental contract linking
  const requiresRentalContract = fileLabel && isRentalRelatedDocument(fileLabel);

  // TODO: Add state for other asset types (renovation projects, building assets)

  // Debug: Monitor selectedFiles state changes
  useEffect(() => {
    console.log('[FileUpload] selectedFiles state changed:', selectedFiles.map(f => ({
      id: f.id,
      name: f.file.name,
      status: f.status
    })));
  }, [selectedFiles]);

  // Handle initial files if provided
  useEffect(() => {
    if (initialFiles && initialFiles.length > 0) {
      addFilesToQueue(initialFiles);
    }
  }, [initialFiles]);

  const handleFileUpload = (e: any) => {
    const files = e.target.files;
    addFilesToQueue(Array.from(files));
  };

  const addFilesToQueue = async (files: File[]) => {
    setIsProcessingImages(true);

    try {
      // Validate file sizes first (max 20MB)
      const maxFileSize = 20 * 1024 * 1024; // 20MB in bytes
      const oversizedFiles = files.filter(file => file.size > maxFileSize);

      if (oversizedFiles.length > 0) {
        oversizedFiles.forEach(file => {
          toast.error(`${file.name} is te groot (${(file.size / 1024 / 1024).toFixed(2)}MB). Maximum is 20MB`, {
            autoClose: 5000
          });
        });

        // Filter out oversized files
        files = files.filter(file => file.size <= maxFileSize);

        // If no valid files remain, exit
        if (files.length === 0) {
          setIsProcessingImages(false);
          return;
        }
      }

      // Correct orientation for image files
      const correctedFiles = await Promise.all(
        files.map(async (file) => {
          try {
            // Only process image files
            if (file.type.startsWith('image/')) {
              return await correctImageOrientation(file);
            }
            return file;
          } catch (error) {
            console.error(`Failed to correct orientation for ${file.name}:`, error);
            toast.warning(`Kon oriëntatie voor ${file.name} niet corrigeren, origineel bestand wordt gebruikt`);
            return file;
          }
        })
      );

      const newFiles = correctedFiles.map(file => {
        fileIdCounter.current += 1;
        const fileId = `file-${Date.now()}-${fileIdCounter.current}`;
        console.log(`[FileUpload] Generated ID for ${file.name}: ${fileId}`);
        return {
          file,
          id: fileId,
          status: 'pending' as const,
          rentalContract: requiresRentalContract ? null : undefined,
          isSelected: false
        };
      });

      console.log('[FileUpload] Adding files to queue:', newFiles.map(f => ({ id: f.id, name: f.file.name })));
      setSelectedFiles(prev => [...prev, ...newFiles]);
    } catch (error) {
      console.error('Error processing files:', error);
      Sentry.captureException(error, {
        tags: { feature: 'fileUpload', method: 'addFilesToQueue' },
        extra: { fileCount: files.length, fileNames: files.map(f => f.name), fileLabel, buildingId, unitId },
      });
      toast.error('Er is een fout opgetreden bij het verwerken van bestanden');
    } finally {
      setIsProcessingImages(false);
    }
  };


  const handleDragOver = (event: any) => {
    event.preventDefault();
  };

  const handleDrop = (event: any) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files.length) {
      addFilesToQueue(Array.from(files));
    }
  };

  const handleContractSelect = (contract: any) => {
    if (editingFileId) {
      // Update specific file
      setSelectedFiles(prev => prev.map(f =>
        f.id === editingFileId
          ? { ...f, rentalContract: contract }
          : f
      ));
    }
    setIsContractPickerOpen(false);
    setEditingFileId(null);
  };

  const openContractPicker = (fileId: string) => {
    setEditingFileId(fileId);
    setIsContractPickerOpen(true);
  };

  const removeFile = (fileId: string) => {
    setSelectedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const uploadAllFiles = async () => {
    console.log('[FileUpload] uploadAllFiles started');
    console.log('[FileUpload] Current selectedFiles:', selectedFiles.map(f => ({ id: f.id, name: f.file.name, status: f.status })));

    // Validate that all rental documents have contracts
    const invalidFiles = selectedFiles.filter(f =>
      requiresRentalContract && !f.rentalContract
    );

    if (invalidFiles.length > 0) {
      toast.error("Alle huurcontract-documenten moeten een contract hebben gekoppeld");
      return;
    }

    // Create a snapshot of files to upload to avoid iteration issues
    const filesToUpload = [...selectedFiles];
    const successfulFileIds: string[] = [];

    console.log('[FileUpload] Files to upload:', filesToUpload.map(f => ({ id: f.id, name: f.file.name })));

    // Upload each file
    for (const fileInfo of filesToUpload) {
      try {
        console.log(`[FileUpload] Starting upload for: ${fileInfo.id} (${fileInfo.file.name})`);

        setSelectedFiles(prev => prev.map(f =>
          f.id === fileInfo.id
            ? { ...f, status: 'uploading' }
            : f
        ));

        await uploadSingleFile(fileInfo);

        setSelectedFiles(prev => prev.map(f =>
          f.id === fileInfo.id
            ? { ...f, status: 'success' }
            : f
        ));

        // Track successfully uploaded files
        successfulFileIds.push(fileInfo.id);
        console.log(`[FileUpload] Successfully uploaded: ${fileInfo.id}`);
        console.log(`[FileUpload] successfulFileIds array now:`, successfulFileIds);
      } catch (error) {
        console.error(`[FileUpload] Error uploading ${fileInfo.id}:`, error);
        Sentry.captureException(error, {
          tags: { feature: 'fileUpload', method: 'uploadAllFiles' },
          extra: { fileId: fileInfo.id, fileName: fileInfo.file.name, fileLabel, buildingId, unitId },
        });
        toast.error(`Fout bij uploaden van ${fileInfo.file.name}: ${error instanceof Error ? error.message : 'Onbekende fout'}`);
        setSelectedFiles(prev => prev.map(f =>
          f.id === fileInfo.id
            ? { ...f, status: 'error' }
            : f
        ));
      }
    }

    console.log('[FileUpload] All uploads complete');
    console.log('[FileUpload] Final successfulFileIds:', successfulFileIds);

    // Remove all successfully uploaded files after showing success status briefly
    if (successfulFileIds.length > 0) {
      console.log(`[FileUpload] Scheduling removal of ${successfulFileIds.length} files in 1 second`);
      setTimeout(() => {
        console.log('[FileUpload] setTimeout callback executing');
        console.log('[FileUpload] successfulFileIds in setTimeout:', successfulFileIds);

        setSelectedFiles(prev => {
          console.log('[FileUpload] Current state before filter:', prev.map(f => ({ id: f.id, name: f.file.name, status: f.status })));
          const remaining = prev.filter(f => {
            const shouldKeep = !successfulFileIds.includes(f.id);
            console.log(`[FileUpload] File ${f.id}: shouldKeep = ${shouldKeep} (includes: ${successfulFileIds.includes(f.id)})`);
            return shouldKeep;
          });
          console.log('[FileUpload] Remaining files after filter:', remaining.map(f => ({ id: f.id, name: f.file.name })));
          return remaining;
        });

        console.log('[FileUpload] Calling onUploadSuccess()');
        try {
          onUploadSuccess();
          console.log('[FileUpload] onUploadSuccess() completed');
        } catch (error) {
          console.error('[FileUpload] Error in onUploadSuccess():', error);
        }
      }, 1000);
    }
  };

  const uploadSingleFile = async (fileInfo: any) => {
    const formData = new FormData();
    formData.append("file", fileInfo.file);

    // Build relations array - include assetId if provided and selected relations
    const relationsArray: any[] = [];
    if (assetId) {
      relationsArray.push({
        entityType: 'asset',
        entityId: assetId
      });
    }

    // Add selected relations (contacts/companies)
    if (selectedRelations.length > 0) {
      selectedRelations.forEach((relation) => {
        relationsArray.push({
          id: relation.id,
          resourceType: relation.type,
          resourceId: relation.resourceId
        });
      });
    }

    // Build file log data
    const fileLogData = {
      fileLabel: fileLabel || "Overig",
      unitId: unitId || null,
      buildingId: buildingId || null,
      resourceTypes: unitId ? ["unit"] : ["building"],
      documentTypes: ["document"],
      status: "UNKNOWN",
      statusDescription: "",
      signStatus: "UNKNOWN",
      summary: "",
      documentDate: null,
      expirationDate: null,
      relations: relationsArray,
    };

    // Add custom data for rental contracts
    let customDataToSend = { ...customData };
    if (requiresRentalContract && fileInfo.rentalContract) {
      customDataToSend.rentalContractId = fileInfo.rentalContract.id;
      if (fileInfo.rentalContract.guarantee && fileInfo.rentalContract.guarantee.length > 0) {
        customDataToSend.rentalGuaranteeId = fileInfo.rentalContract.guarantee[0].id;
      }
    }

    // Add acquisitionId if an acquisition file is linked
    if (selectedAcquisitionFile?.id) {
      customDataToSend.acquisitionId = selectedAcquisitionFile.id;
    }

    formData.append("fileLog", JSON.stringify(fileLogData));
    formData.append("customData", JSON.stringify(customDataToSend));

    const response = await fetchWithAuth(
      `${API_URL}/file/log`,
      {
        method: "POST",
        body: formData,
      },
      session,
    );

    if (!response.ok) {
      let errorMessage = `Upload mislukt (status ${response.status})`;
      try {
        const errorBody = await response.json();
        errorMessage = errorBody.message || errorMessage;
      } catch {
        // Response body is not JSON, use default message
      }
      const error = new Error(errorMessage);
      Sentry.captureException(error, {
        tags: { feature: 'fileUpload', method: 'uploadSingleFile' },
        extra: {
          fileName: fileInfo.file.name,
          fileSize: fileInfo.file.size,
          fileLabel,
          buildingId,
          unitId,
          statusCode: response.status,
          statusText: response.statusText,
        },
      });
      throw error;
    }

    return response.json();
  };

  // Picture mode specific functions
  const toggleFileSelection = (index: number, selected: boolean) => {
    setSelectedIndexes(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(index);
      } else {
        newSet.delete(index);
      }
      return newSet;
    });
  };

  const selectAllFiles = () => {
    if (selectedIndexes.size === selectedFiles.length) {
      setSelectedIndexes(new Set());
    } else {
      setSelectedIndexes(new Set(selectedFiles.map((_, index) => index)));
    }
  };

  const uploadSingleFileByIndex = async (index: number) => {
    const fileInfo = selectedFiles[index];
    if (!fileInfo) return;

    if (requiresRentalContract && !fileInfo.rentalContract) {
      toast.error("Dit bestand heeft een huurcontract nodig");
      return;
    }

    try {
      setSelectedFiles(prev => prev.map(f =>
        f.id === fileInfo.id
          ? { ...f, status: 'uploading' }
          : f
      ));

      await uploadSingleFile(fileInfo);

      setSelectedFiles(prev => prev.map(f =>
        f.id === fileInfo.id
          ? { ...f, status: 'success' }
          : f
      ));

      // Clear successful upload after delay
      setTimeout(() => {
        setSelectedFiles(prev => prev.filter(f => f.id !== fileInfo.id));
        onUploadSuccess();
      }, 1000);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'fileUpload', method: 'uploadSingleFileByIndex' },
        extra: { index, fileId: fileInfo.id, fileName: fileInfo.file.name, fileLabel, buildingId, unitId },
      });
      toast.error(`Fout bij uploaden van ${fileInfo.file.name}: ${error instanceof Error ? error.message : 'Onbekende fout'}`);
      setSelectedFiles(prev => prev.map(f =>
        f.id === fileInfo.id
          ? { ...f, status: 'error' }
          : f
      ));
    }
  };

  // Check if file is an image
  const isImageFile = (file: File) => {
    return file.type.startsWith('image/');
  };

  // Determine if we should show picture mode interface
  const showPictureMode = mode === 'picture' || selectedFiles.some(f => isImageFile(f.file));

  // Intersection Observer for tracking visible images on mobile
  useEffect(() => {
    if (!showPictureMode || selectedFiles.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = imageRefs.current.findIndex(ref => ref === entry.target);
            if (index !== -1) {
              setVisibleImageIndex(index);
            }
          }
        });
      },
      {
        threshold: 0.5, // Trigger when 50% of image is visible
        rootMargin: '-60px 0px 0px 0px' // Account for sticky header
      }
    );

    imageRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [showPictureMode, selectedFiles.length]);

  return (
    <div className="relative -mx-3">
      {/* Mobile Picture Mode: Sticky Header */}
      {showPictureMode && selectedFiles.length > 0 && (
        <div className="md:hidden sticky top-0 z-40 bg-white border-b border-neutral-200 shadow-md -mt-3">
          {/* Header with Back Button */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-neutral-100">
            {onClose && (
              <button
                onClick={onClose}
                className="shrink-0 p-2 -ml-2 hover:bg-neutral-100 rounded-full transition-colors touch-manipulation"
                aria-label="Terug"
              >
                <svg className="w-6 h-6 text-neutral-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-neutral-primary truncate">
                {customHeaderTitle}
              </h2>
              <p className="text-xs text-neutral-secondary-mid">
                {visibleImageIndex + 1} van {selectedFiles.length}
              </p>
            </div>
            {/* Upload Status Indicator */}
            {selectedFiles[visibleImageIndex]?.status === 'uploading' && (
              <svg className="h-5 w-5 text-moss-primary animate-spin shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            {selectedFiles[visibleImageIndex]?.status === 'success' && (
              <svg className="h-6 w-6 text-moss-primary shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>

          {/* Action Bar */}
          <div className="px-4 py-3 flex gap-2 bg-neutral-50">
            <button
              onClick={() => uploadSingleFileByIndex(visibleImageIndex)}
              disabled={
                selectedFiles[visibleImageIndex]?.status === 'uploading' ||
                selectedFiles[visibleImageIndex]?.status === 'success' ||
                (!!requiresRentalContract && !selectedFiles[visibleImageIndex]?.rentalContract)
              }
              className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-moss-primary text-white rounded-xl hover:bg-moss-primaryDark disabled:opacity-50 disabled:bg-neutral-300 disabled:cursor-not-allowed transition-all text-sm font-semibold min-h-[50px] touch-manipulation shadow-sm active:scale-[0.98]"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              {selectedFiles[visibleImageIndex]?.status === 'success' ? 'Geüpload!' : 'Upload foto'}
            </button>
            <button
              onClick={() => removeFile(selectedFiles[visibleImageIndex]?.id)}
              className="px-5 py-3.5 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-all min-h-[50px] touch-manipulation shadow-sm active:scale-[0.98]"
              aria-label="Verwijder"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>

          {/* Contract Picker - Mobile */}
          {requiresRentalContract && selectedFiles[visibleImageIndex] && (
            <div className="px-4 pb-3 bg-neutral-50 border-t border-neutral-100">
              {selectedFiles[visibleImageIndex].rentalContract ? (
                <div className="flex items-center justify-between p-3 bg-moss-primary/10 rounded-xl border border-moss-primary/30">
                  <div className="text-sm flex-1 min-w-0">
                    <div className="font-semibold text-moss-primary truncate">
                      {selectedFiles[visibleImageIndex].rentalContract.contactName || 'Huurcontract'}
                    </div>
                    <div className="text-neutral-secondary-mid text-xs mt-0.5">
                      Unit {selectedFiles[visibleImageIndex].rentalContract.unitId || unitId}
                    </div>
                  </div>
                  <button
                    onClick={() => openContractPicker(selectedFiles[visibleImageIndex].id)}
                    className="px-4 py-2 text-sm text-brick-primary hover:text-brick-primaryDark font-semibold ml-2 min-h-[44px]"
                  >
                    Wijzig
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => openContractPicker(selectedFiles[visibleImageIndex].id)}
                  className="w-full px-4 py-3.5 border-2 border-brick-primary/40 text-brick-primary rounded-xl text-sm hover:bg-brick-primary/5 hover:border-brick-primary transition-colors font-semibold min-h-[50px] touch-manipulation"
                >
                  + Koppel huurcontract
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Content Area */}
      <div className="px-3">
        {/* Files Display - Different layouts for document vs picture mode */}
        {selectedFiles.length > 0 && (
        <div className="mb-4">
          {!showPictureMode ? (
            // Document mode - List view
            <>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-lg font-medium text-neutral-primary">
                  Geselecteerde bestanden ({selectedFiles.length})
                </h3>
                <button
                  onClick={uploadAllFiles}
                  disabled={selectedFiles.some(f => requiresRentalContract && !f.rentalContract)}
                  className="px-4 py-2 bg-moss-primary text-white rounded-lg hover:bg-moss-primaryDark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Upload alle bestanden
                </button>
              </div>

          <div className="border border-neutral-200 rounded-lg overflow-hidden">
            <div className="space-y-2 p-3">
              {selectedFiles.map((fileInfo) => (
                <div
                  key={fileInfo.id}
                  className="flex items-center gap-3 p-3 bg-white border rounded-lg"
                >
                  {/* File Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-neutral-secondary-mid shrink-0" />
                      <span className="text-sm font-medium text-neutral-primary truncate">
                        {fileInfo.file.name}
                      </span>
                      <span className="text-xs text-neutral-secondary-mid">
                        ({(fileInfo.file.size / 1024 / 1024).toFixed(1)} MB)
                      </span>
                    </div>

                    {/* Status */}
                    <div className="mt-1 flex items-center gap-2">
                      {fileInfo.status === 'pending' && (
                        <span className="text-xs text-neutral-secondary-mid">Klaar voor upload</span>
                      )}
                      {fileInfo.status === 'uploading' && (
                        <span className="text-xs text-brick-primary">Uploaden...</span>
                      )}
                      {fileInfo.status === 'success' && (
                        <span className="text-xs text-moss-primary">✓ Geüpload</span>
                      )}
                      {fileInfo.status === 'error' && (
                        <span className="text-xs text-red-500">✗ Fout bij uploaden</span>
                      )}
                    </div>
                  </div>

                  {/* Rental Contract */}
                  {requiresRentalContract && (
                    <div className="shrink-0">
                      {fileInfo.rentalContract ? (
                        <div className="flex items-center gap-2">
                          <div className="text-xs">
                            <div className="font-medium text-moss-primary">
                              {fileInfo.rentalContract.contactName || 'Huurcontract'}
                            </div>
                            <div className="text-neutral-secondary-mid">
                              Unit {fileInfo.rentalContract.unitId || unitId}
                            </div>
                          </div>
                          <button
                            onClick={() => openContractPicker(fileInfo.id)}
                            className="text-xs text-brick-primary hover:text-brick-primaryDark"
                          >
                            Wijzigen
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => openContractPicker(fileInfo.id)}
                          className="px-3 py-1 border border-brick-primary/30 text-brick-primary rounded-sm text-xs hover:bg-brick-secondary-fine transition-colors"
                        >
                          + Contract
                        </button>
                      )}
                    </div>
                  )}

                  {/* Remove Button */}
                  <button
                    onClick={() => removeFile(fileInfo.id)}
                    className="shrink-0 p-1 text-neutral-secondary-mid hover:text-red-500 transition-colors"
                    title="Verwijder bestand"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
            </>
          ) : (
            // Picture mode - Instagram-like mobile experience
            <>
              {/* Desktop Header - Hidden on Mobile */}
              <div className="mb-3 hidden md:flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-medium text-neutral-primary">
                    Afbeeldingen ({selectedFiles.length})
                  </h3>
                  {selectedFiles.length > 1 && (
                    <button
                      onClick={selectAllFiles}
                      className="text-sm text-moss-primary hover:text-moss-primaryDark transition-colors"
                    >
                      {selectedIndexes.size === selectedFiles.length ? 'Deselecteer alles' : 'Selecteer alles'}
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  {selectedIndexes.size > 0 && (
                    <button
                      onClick={async () => {
                        const indicesToUpload = Array.from(selectedIndexes);
                        const filesToUpload = indicesToUpload.map(index => selectedFiles[index]).filter(Boolean);

                        // Validate files
                        const hasInvalidFiles = filesToUpload.some(f => requiresRentalContract && !f.rentalContract);
                        if (hasInvalidFiles) {
                          toast.error("Alle huurcontract-documenten moeten een contract hebben gekoppeld");
                          return;
                        }

                        const successfulFileIds: string[] = [];

                        // Upload each selected file
                        for (const fileInfo of filesToUpload) {
                          try {
                            setSelectedFiles(prev => prev.map(f =>
                              f.id === fileInfo.id ? { ...f, status: 'uploading' } : f
                            ));

                            await uploadSingleFile(fileInfo);

                            setSelectedFiles(prev => prev.map(f =>
                              f.id === fileInfo.id ? { ...f, status: 'success' } : f
                            ));

                            successfulFileIds.push(fileInfo.id);
                          } catch (error) {
                            Sentry.captureException(error, {
                              tags: { feature: 'fileUpload', method: 'bulkUploadSelected' },
                              extra: { fileId: fileInfo.id, fileName: fileInfo.file.name, fileLabel, buildingId, unitId },
                            });
                            toast.error(`Fout bij uploaden van ${fileInfo.file.name}: ${error instanceof Error ? error.message : 'Onbekende fout'}`);
                            setSelectedFiles(prev => prev.map(f =>
                              f.id === fileInfo.id ? { ...f, status: 'error' } : f
                            ));
                          }
                        }

                        // Remove all successfully uploaded files after showing success status
                        if (successfulFileIds.length > 0) {
                          setTimeout(() => {
                            setSelectedFiles(prev => prev.filter(f => !successfulFileIds.includes(f.id)));
                            onUploadSuccess();
                          }, 1000);
                        }
                      }}
                      disabled={Array.from(selectedIndexes).some(index => {
                        const file = selectedFiles[index];
                        return requiresRentalContract && !file?.rentalContract;
                      })}
                      className="px-3 py-2 bg-moss-primary text-white rounded-lg hover:bg-moss-primaryDark disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                    >
                      Upload geselecteerd ({selectedIndexes.size})
                    </button>
                  )}
                  <button
                    onClick={uploadAllFiles}
                    disabled={selectedFiles.some(f => requiresRentalContract && !f.rentalContract)}
                    className="px-3 py-2 bg-neutral-primary text-white rounded-lg hover:bg-neutral-primaryDark disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                  >
                    Upload alles
                  </button>
                </div>
              </div>

              {/* Picture Grid - Mobile: Single column, Desktop: Grid */}
              <div className="flex flex-col md:grid md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
                {selectedFiles.map((fileInfo, index) => (
                  <div
                    key={fileInfo.id}
                    ref={el => imageRefs.current[index] = el}
                    className="relative group"
                  >
                    {/* Mobile: Full width image card */}
                    <div className="md:hidden">
                      {/* Upload progress overlay */}
                      {fileInfo.status === 'uploading' && (
                        <div className="absolute inset-0 bg-moss-primary/20 rounded-lg flex items-center justify-center z-20">
                          <div className="bg-white rounded-full p-3 shadow-lg">
                            <svg className="h-6 w-6 text-moss-primary animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          </div>
                        </div>
                      )}

                      {/* Success overlay */}
                      {fileInfo.status === 'success' && (
                        <div className="absolute inset-0 bg-moss-primary/20 rounded-lg flex items-center justify-center z-20">
                          <div className="bg-white rounded-full p-3 shadow-lg">
                            <svg className="h-6 w-6 text-moss-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        </div>
                      )}

                      {/* Error overlay */}
                      {fileInfo.status === 'error' && (
                        <div className="absolute inset-0 bg-red-500/20 rounded-lg flex items-center justify-center z-20">
                          <div className="bg-white rounded-full p-3 shadow-lg">
                            <svg className="h-6 w-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </div>
                        </div>
                      )}

                      <div className={`relative overflow-hidden rounded-lg border-2 ${
                        visibleImageIndex === index ? 'border-moss-primary' : 'border-transparent'
                      } ${
                        fileInfo.status === 'uploading' ? 'animate-pulse' : ''
                      }`}>
                        {isImageFile(fileInfo.file) ? (
                          <img
                            src={URL.createObjectURL(fileInfo.file)}
                            alt={fileInfo.file.name}
                            className="w-full aspect-square object-cover"
                          />
                        ) : (
                          <div className="w-full aspect-square bg-gray-100 flex items-center justify-center">
                            <FileText className="w-12 h-12 text-gray-400" />
                          </div>
                        )}
                      </div>

                      {/* Mobile: File info below */}
                      <div className="mt-2 pb-4 border-b border-neutral-100">
                        <p className="text-sm text-gray-600 font-medium">{fileInfo.file.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {(fileInfo.file.size / 1024 / 1024).toFixed(1)} MB
                        </p>
                      </div>
                    </div>

                    {/* Desktop: Grid card view (original design) */}
                    <div className="hidden md:block">
                      {/* Selection checkbox */}
                      <div className="absolute top-2 left-2 z-10">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded-sm border border-neutral-primary/30 cursor-pointer"
                          style={{ accentColor: '#5B7304' }}
                          checked={selectedIndexes.has(index)}
                          onChange={(e) => toggleFileSelection(index, e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>

                      {/* Upload progress overlay */}
                      {fileInfo.status === 'uploading' && (
                        <div className="absolute inset-0 bg-moss-primary/20 rounded-lg flex items-center justify-center z-20">
                          <div className="bg-white rounded-full p-2 shadow-md">
                            <svg className="h-4 w-4 text-moss-primary animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          </div>
                        </div>
                      )}

                      {/* Success overlay */}
                      {fileInfo.status === 'success' && (
                        <div className="absolute inset-0 bg-moss-primary/20 rounded-lg flex items-center justify-center z-20">
                          <div className="bg-white rounded-full p-2 shadow-md">
                            <svg className="h-4 w-4 text-moss-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        </div>
                      )}

                      {/* Error overlay */}
                      {fileInfo.status === 'error' && (
                        <div className="absolute inset-0 bg-red-500/20 rounded-lg flex items-center justify-center z-20">
                          <div className="bg-white rounded-full p-2 shadow-md">
                            <svg className="h-4 w-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </div>
                        </div>
                      )}

                      <div className={`relative overflow-hidden rounded-lg ${
                        selectedIndexes.has(index) ? 'ring-2 ring-moss-primary' : ''
                      } ${
                        fileInfo.status === 'uploading' ? 'animate-pulse' : ''
                      }`}>
                        {isImageFile(fileInfo.file) ? (
                          <img
                            src={URL.createObjectURL(fileInfo.file)}
                            alt={fileInfo.file.name}
                            className="h-32 w-full object-cover"
                          />
                        ) : (
                          <div className="h-32 w-full bg-gray-100 flex items-center justify-center">
                            <FileText className="w-8 h-8 text-gray-400" />
                          </div>
                        )}

                        {/* Action buttons */}
                        <div className="absolute top-2 right-2 flex gap-1">
                          <button
                            onClick={() => uploadSingleFileByIndex(index)}
                            disabled={fileInfo.status === 'uploading' || (!!requiresRentalContract && !fileInfo.rentalContract)}
                            className={`rounded-full p-1.5 transition-all touch-manipulation ${
                              fileInfo.status === 'uploading' || (!!requiresRentalContract && !fileInfo.rentalContract)
                                ? 'bg-gray-400 text-gray-300 cursor-not-allowed opacity-50'
                                : 'bg-moss-primary text-white hover:bg-moss-primaryDark shadow-sm'
                            }`}
                            title={fileInfo.status === 'uploading' ? "Uploading..." : "Upload bestand"}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                          </button>
                          <button
                            onClick={() => removeFile(fileInfo.id)}
                            className="bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 touch-manipulation"
                            title="Verwijder bestand"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      {/* File info */}
                      <div className="mt-2 space-y-1">
                        <p className="text-xs text-gray-600 truncate font-medium">{fileInfo.file.name}</p>
                        <p className="text-xs text-gray-500">
                          {(fileInfo.file.size / 1024 / 1024).toFixed(1)} MB
                        </p>

                        {/* Rental Contract Info */}
                        {requiresRentalContract && (
                          <div className="mt-1">
                            {fileInfo.rentalContract ? (
                              <div className="text-xs">
                                <div className="font-medium text-moss-primary truncate">
                                  {fileInfo.rentalContract.contactName || 'Huurcontract'}
                                </div>
                                <button
                                  onClick={() => openContractPicker(fileInfo.id)}
                                  className="text-xs text-brick-primary hover:text-brick-primaryDark"
                                >
                                  Wijzigen
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => openContractPicker(fileInfo.id)}
                                className="w-full px-2 py-1 border border-brick-primary/30 text-brick-primary rounded-sm text-xs hover:bg-brick-secondary-fine transition-colors"
                              >
                                + Contract
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Drop Zone */}
      <div
        ref={dropZoneRef}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={`flex flex-col items-center gap-4 rounded-md border-2 border-dashed p-4 touch-manipulation ${
          isProcessingImages ? 'opacity-50 pointer-events-none' : ''
        }`}
      >
        {isProcessingImages ? (
          <>
            <div className="flex items-center gap-2">
              <svg className="h-5 w-5 text-moss-primary animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <p className="text-center text-sm sm:text-base font-medium text-moss-primary">
                Afbeeldingen worden verwerkt...
              </p>
            </div>
          </>
        ) : (
          <>
            <p className="text-center text-sm sm:text-base">
              {showPictureMode
                ? "Sleep afbeeldingen of klik op de knop om foto's toe te voegen (max. 20 Mb)"
                : "Sleep een bestand of klik op de knop om een of meerdere documenten toe te voegen (max. 20 Mb)"
              }
            </p>
            <input
              type="file"
              multiple
              accept={showPictureMode ? "image/*" : undefined}
              onChange={handleFileUpload}
              style={{ display: "none" }}
              id="fileInput"
            />
            <label
              htmlFor="fileInput"
              className="flex cursor-pointer items-center gap-1.5 rounded-full bg-neutral-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50 touch-manipulation min-h-[44px]"
            >
              {showPictureMode ? "Selecteer foto's" : "Selecteer bestand"}
            </label>
          </>
        )}
      </div>

        {/* Expandable "Gelinkte dossiers" section - shown in both modes */}
        <div className="mt-4 border border-neutral-200 rounded-lg overflow-hidden">
          <button
            onClick={() => setIsLinkedDossiersExpanded(!isLinkedDossiersExpanded)}
            className="w-full flex items-center justify-between p-3 bg-neutral-50 hover:bg-neutral-100 transition-colors touch-manipulation"
          >
            <div className="flex items-center gap-2">
              <LinkSimple size={18} weight="duotone" className="text-neutral-primary" />
              <span className="font-medium text-neutral-primary">Gekoppelde relaties</span>
              {(selectedAcquisitionFile || selectedRelations.length > 0) && (
                <span className="text-xs bg-moss-primary/10 text-moss-primary px-2 py-0.5 rounded-full">
                  {(selectedAcquisitionFile ? 1 : 0) + selectedRelations.length} gekoppeld
                </span>
              )}
            </div>
            {isLinkedDossiersExpanded ? (
              <CaretUp size={18} className="text-neutral-secondary-mid" />
            ) : (
              <CaretDown size={18} className="text-neutral-secondary-mid" />
            )}
          </button>

          {isLinkedDossiersExpanded && (
            <div className="p-3 space-y-4">
              {/* Relations (Contacts/Companies) linking */}
              <div>
                <label className="text-sm text-neutral-secondary-mid mb-2 block">
                  Contacten & Bedrijven
                </label>
                {selectedRelations.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {selectedRelations.map((relation) => (
                      <ResourceChip
                        key={`${relation.type}-${relation.id}`}
                        resource={relation}
                        onRemove={() => {
                          setSelectedRelations((prev) =>
                            prev.filter((r) => r.id !== relation.id || r.type !== relation.type)
                          );
                        }}
                      />
                    ))}
                  </div>
                )}
                <button
                  onClick={() => setIsRelationsPickerOpen(true)}
                  className="w-full px-3 py-2.5 border border-dashed border-neutral-300 rounded-lg text-sm text-neutral-secondary-mid hover:border-moss-primary hover:text-moss-primary transition-colors flex items-center justify-center gap-2 touch-manipulation min-h-[44px]"
                >
                  <User size={16} weight="duotone" />
                  + Koppel contact of bedrijf
                </button>
              </div>

              {/* Acquisitiedossier linking - only show in picture mode */}
              {showPictureMode && (
                <div>
                  <label className="text-sm text-neutral-secondary-mid mb-2 block">
                    Acquisitiedossier
                  </label>
                  {selectedAcquisitionFile ? (
                    <div className="flex items-center justify-between p-3 bg-moss-primary/5 rounded-lg border border-moss-primary/20">
                      <div className="flex items-center gap-2">
                        <Folder size={16} weight="duotone" className="text-moss-primary" />
                        <span className="text-sm font-medium text-moss-primary">
                          #{selectedAcquisitionFile.name || `ACQ-${selectedAcquisitionFile.id}`}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setIsAcquisitionPickerOpen(true)}
                          className="text-xs text-brick-primary hover:text-brick-primaryDark font-medium min-h-[44px] px-2"
                        >
                          Wijzig
                        </button>
                        <button
                          onClick={() => setSelectedAcquisitionFile(null)}
                          className="text-xs text-neutral-secondary-mid hover:text-red-500 font-medium min-h-[44px] px-2"
                        >
                          Verwijder
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setIsAcquisitionPickerOpen(true)}
                      className="w-full px-3 py-2.5 border border-dashed border-neutral-300 rounded-lg text-sm text-neutral-secondary-mid hover:border-moss-primary hover:text-moss-primary transition-colors flex items-center justify-center gap-2 touch-manipulation min-h-[44px]"
                    >
                      <Folder size={16} weight="duotone" />
                      + Koppel acquisitiedossier
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Rental Contract Selection Modal */}
        <UniversalPickerModal
          isOpen={isContractPickerOpen}
          onClose={() => setIsContractPickerOpen(false)}
          mode="rental-contract"
          title="Selecteer huurcontract"
          onConfirm={handleContractSelect}
          config={{
            apiConfig: {
              endpoint: `/search/rental/unit/${unitId}/contracts`,
              method: 'GET',
              displayField: 'contactName',
              valueField: 'id',
              secondaryField: 'unitId',
              dataPath: '', // Contracts should be in root array
              placeholderValues: {
                unitId: unitId || ''
              }
            },
            searchPlaceholder: "Zoek huurcontract...",
            allowRemoval: false
          }}
        />

        {/* Acquisition File Selection Modal */}
        <UniversalPickerModal
          isOpen={isAcquisitionPickerOpen}
          onClose={() => setIsAcquisitionPickerOpen(false)}
          mode="acquisition-file"
          title="Selecteer acquisitiedossier"
          onConfirm={(acquisition: any) => {
            setSelectedAcquisitionFile(acquisition);
            setIsAcquisitionPickerOpen(false);
          }}
          config={{
            apiConfig: {
              endpoint: `/acquisitions`,
              method: 'GET',
              displayField: 'name',
              valueField: 'id',
              secondaryField: 'id',
              dataPath: ''
            },
            searchPlaceholder: "Zoek acquisitiedossier...",
            allowRemoval: true
          }}
        />

        {/* Relations (Contacts/Companies) Selection Modal */}
        <UniversalPickerModal
          isOpen={isRelationsPickerOpen}
          onClose={() => setIsRelationsPickerOpen(false)}
          mode="resource"
          title="Koppel contact of bedrijf"
          onConfirm={(resource: ResourceData) => {
            // Check if already selected (avoid duplicates)
            const isAlreadySelected = selectedRelations.some(
              (r) => r.id === resource.id && r.type === resource.type
            );
            if (!isAlreadySelected) {
              setSelectedRelations((prev) => [...prev, resource]);
            }
            setIsRelationsPickerOpen(false);
          }}
          config={{
            resourceTypeFilter: ['contact', 'company'], // Only show contacts and companies
            searchPlaceholder: "Zoek contact of bedrijf...",
            allowRemoval: false
          }}
        />
      </div>
    </div>
  );
};

export default FileUploadComponent;
